import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Image from 'components/Image';
import Link from 'components/Link';
import isBrowser from 'utils/is-browser';
import isScrolledIntoView from 'utils/is-scrolled-into-view';
import xss from 'utils/xss';
import styles from './ContentMega.module.scss';

const classnameAlignment = {
  center: 'justify-content-center text-center px-5',
  left: 'justify-content-end text-start pb-xl-30 ps-xl-50 py-15 px-15',
};
/* eslint-disable-next-line complexity */
export function ContentMega({
  alignment: propAlignment,
  anchorId,
  backgroundColor,
  backgroundImage: { title, url } = {},
  backgroundImageDesktop: { url: urlDesktop } = {},
  banner_type: bannerType,
  children,
  ctaText,
  ctaUrl,
  heading,
  headingTag,
  isSetDangerously = false,
  linkText,
  linkUrl,
  overrideBannerType = false,
  parallax,
  smallHeading,
  subHeading,
  subHeadingTag,
  theme,
  useSmallHeading = false,
}) {
  const alignment = propAlignment || 'center';
  const HeadingTag = headingTag || 'h1';
  const SubHeadingTag = subHeadingTag || 'p';

  // mega by default
  const isHero = bannerType?.toLowerCase() === 'hero';
  const isMega = bannerType?.toLowerCase() === 'mega';
  const isSmallHero = !isHero && !isMega;
  const isCentered = alignment === 'center';

  const [top, setTop] = useState(0);
  const node = useRef();

  const ctaStyle = linkUrl ? 'me-13' : 'me-0';

  const useDarkText = theme?.toLowerCase() === 'primary';

  useEffect(() => {
    if (isBrowser && parallax) {
      const updatePosition = () => {
        const bound = node.current.getBoundingClientRect();
        const { top } = bound;
        if (top < 0) {
          setTop(-top);
        } else {
          setTop(0);
        }
      };

      const onScroll = () => {
        if (node.current) {
          const isInView = isScrolledIntoView(node.current);
          if (isInView) {
            window.requestAnimationFrame(updatePosition);
          }
        }
      };
      window.addEventListener('scroll', onScroll);

      return () => {
        window.removeEventListener('scroll', onScroll);
      };
    }
  }, [parallax]);

  const subHeadingTextSanitized = isSetDangerously ? (
    // eslint-disable-next-line react/no-danger
    <div dangerouslySetInnerHTML={{ __html: xss(subHeading) }} />
  ) : (
    <SubHeadingTag className="font-roboto fs-12 mb-0">
      {subHeading}
    </SubHeadingTag>
  );

  const bannerAlignment = classnameAlignment[alignment];

  const subHeadingStyle =
    alignment === 'center'
      ? `${styles.sub_header} ${styles.sub_header_center}`
      : `${styles.sub_header}`;

  const bgColor = backgroundColor ? { background: backgroundColor } : {};

  return (
    <section
      ref={node}
      className={classNames('position-relative', {
        [styles.mega_section]: isMega,
        [styles.hero_section]: isHero,
        [styles.small_hero_section]: isSmallHero,
        'overflow-hidden': parallax,
      })}
      id={anchorId}
      style={bgColor}
    >
      <div className="position-absolute h-100 w-100" style={{ top }}>
        <div className="d-none d-sm-block">
          <Image
            fill
            priority
            src={urlDesktop}
            alt={title}
            objectFit="cover"
            objectPosition={isMega ? 'center center' : 'center top'}
            role="presentation"
          />
        </div>
        <div className="d-sm-none">
          <Image
            fill
            priority
            src={url}
            alt={title}
            objectFit="cover"
            objectPosition="center top"
            role="presentation"
          />
        </div>
      </div>
      <div
        className={classNames(
          styles.content,
          'position-absolute d-flex flex-column w-100 h-100',
          bannerAlignment
        )}
      >
        {smallHeading && (
          <p
            className={classNames(
              'font-sofia fw-700 mb-4 text-uppercase',
              styles.max_width,
              {
                [styles.darkText]: useDarkText,
                [styles.lightText]: !useDarkText,
                [styles.smallHeading]: isMega,
                [styles.heading_width]: isCentered,
                'fs-8 fs-xl-9': isHero || isSmallHero,
              }
            )}
          >
            {smallHeading}
          </p>
        )}
        <div
          className={classNames({
            [styles.small]: overrideBannerType && useSmallHeading,
            [styles.large]: overrideBannerType && !useSmallHeading,
          })}
        >
          <HeadingTag
            className={classNames('mb-0 lh-1', styles.max_width, {
              [styles.darkText]: useDarkText,
              [styles.lightText]: !useDarkText,
              [styles.h1_mega]: !overrideBannerType && isMega,
              [styles.h1_hero]: !overrideBannerType && (isHero || isSmallHero),
              [styles.h1_small_hero]: !overrideBannerType && isSmallHero,
              [styles.heading_width]: isCentered,
            })}
          >
            {heading}
          </HeadingTag>
        </div>
        {subHeading && (
          <div
            className={classNames(
              'font-roboto fs-8 fs-xl-10 fw-400 lh-sm mt-8',
              styles.max_width,
              subHeadingStyle,
              {
                [styles.darkText]: useDarkText,
                [styles.lightText]: !useDarkText,
              }
            )}
          >
            {subHeadingTextSanitized}
          </div>
        )}
        {(ctaUrl || linkUrl) && (
          <div
            className={classNames('d-flex mt-8', {
              'justify-content-center': isCentered,
              'justify-content-left': !isCentered,
            })}
          >
            {ctaUrl && (
              <Link
                href={ctaUrl}
                className={classNames(
                  'font-sofia fw-700 text-primary text-uppercase btn btn-secondary px-15 py-8',
                  ctaStyle
                )}
                data-value="content-page-cta-element"
              >
                {ctaText}
              </Link>
            )}
            {linkUrl && (
              <Link
                href={linkUrl}
                className={classNames('font-sofia fw-700  text-uppercase', {
                  'py-8': ctaUrl,
                  [styles.darkText]: useDarkText,
                  [styles.lightText]: !useDarkText,
                })}
                data-value="content-page-cta-element"
              >
                {linkText}
              </Link>
            )}
          </div>
        )}
        <div>{children}</div>
      </div>
    </section>
  );
}

export default ContentMega;
